import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { auth } from '../lib/firebase'

interface AuthValue {
  user: User | null
  loading: boolean
  signInEmail: (email: string, password: string) => Promise<void>
  signUpEmail: (email: string, password: string) => Promise<void>
  signInGoogle: () => Promise<void>
  signInWithCode: (token: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const Ctx = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u)
        setLoading(false)
      }),
    [],
  )

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      signInEmail: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password)
      },
      signUpEmail: async (email, password) => {
        await createUserWithEmailAndPassword(auth, email, password)
      },
      // Popup rather than redirect: signInWithRedirect is broken on browsers
      // that block third-party storage (Chrome M115+, Firefox 109+, Safari 16.1+).
      signInGoogle: async () => {
        await signInWithPopup(auth, new GoogleAuthProvider())
      },
      // The widget's route in: a pairing code is exchanged server-side for a
      // custom token, giving the iframe an ordinary Firebase session.
      signInWithCode: async (token) => {
        await signInWithCustomToken(auth, token)
      },
      /**
       * Firebase sends the email and handles the link itself. It answers the
       * same way whether or not the address exists, which is deliberate on
       * their side: a different answer would tell a stranger who has an
       * account here.
       */
      resetPassword: async (email) => {
        await sendPasswordResetEmail(auth, email)
      },
      signOut: async () => {
        await fbSignOut(auth)
      },
    }),
    [user, loading],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
